import { useApp } from '@/contexts';

import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type { Environment } from '@/types';
import { logger } from '@/utils/logger';

import {
  useDeleteEnvironmentMutation,
  useGetCredentialsQuery,
  useGetEnvironmentsQuery,
  useToggleEnvironmentPauseMutation,
  useUpdateEnvironmentMutation,
} from '../api/environmentsApi';

interface EnvironmentCardProps {
  environment: Environment;
  onClick: () => void;
  tenantId: string;
}

const ENV_COLORS = [
  '#404040',
  '#334e24',
  '#1c5e67',
  '#412a2e',
  '#006B76',
  '#1273DE',
  '#004DCF',
  '#5300EB',
  '#EB9694',
  '#FAD0C3',
  '#FEF3BD',
  '#C1E1C5',
  '#BEDADC',
  '#C4DEF6',
  '#BED3F3',
  '#FFFFFF',
];

type EditEnvironmentFormData = {
  name: string;
  color: string;
};

/**
 * Individual environment card - clickable to open full detail view
 */
export const EnvironmentCard = ({ environment, onClick, tenantId }: EnvironmentCardProps) => {
  const { appId } = useApp();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch all environments for duplicate name validation
  const { data: environments } = useGetEnvironmentsQuery(
    { tenantId, appId },
    { skip: !tenantId || !appId }
  );

  /**
   * Dynamic validation schema with duplicate name check
   * Validates as user types (real-time validation)
   * Allows keeping the same name (editing color only)
   */
  const editEnvironmentSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, 'Environment name is required')
          .regex(/^[a-zA-Z0-9_\s-]+$/, 'Environment name must not contain special characters')
          .refine(
            (name) => {
              const trimmedName = name.trim();
              // Allow keeping the same name (case-insensitive comparison)
              if (trimmedName.toLowerCase() === environment.name.toLowerCase()) {
                return true;
              }
              // Check if the new name conflicts with another environment
              const isDuplicate = environments?.some(
                (env) =>
                  env.id !== environment.id && env.name.toLowerCase() === trimmedName.toLowerCase()
              );
              return !isDuplicate;
            },
            {
              message: 'Environment name already exists. Please choose a different name.',
            }
          ),
        color: z.string(),
      }),
    [environments, environment.name, environment.id]
  );

  // React Hook Form for edit environment dialog with validation
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<EditEnvironmentFormData>({
    resolver: zodResolver(editEnvironmentSchema),
    mode: 'onChange',
    defaultValues: {
      name: environment.name,
      color: environment.color || ENV_COLORS[0],
    },
  });

  const editColor = watch('color');

  const [updateEnvironment, { isLoading: isUpdating }] = useUpdateEnvironmentMutation();
  const [deleteEnvironment, { isLoading: isDeleting }] = useDeleteEnvironmentMutation();
  const [togglePause] = useToggleEnvironmentPauseMutation();

  // Fetch credentials to get connector count
  const { data: credentials } = useGetCredentialsQuery(
    {
      tenantId,
      appId,
      envName: environment.name,
    },
    { skip: !tenantId || !appId }
  );

  // Filter out AdapterSecret, EnvironmentVariables, and EnvVars from connector count
  // Matches dataProtect envBars.js + transformer_api CredCount logic
  const isEnvVarsCredential = (type: string) =>
    type === 'EnvironmentVariables' || type === 'EnvVars';

  const connectorCount =
    credentials?.filter(
      (c) => c.credentialType !== 'AdapterSecret' && !isEnvVarsCredential(c.credentialType)
    ).length || 0;

  // Count environment variable entries (from credentialsJson.envVars array)
  // Matches dataProtect: "10 Environment Variables" = count of key-value pairs, not credentials
  const envVarsCount =
    credentials?.reduce((sum, c) => {
      if (!isEnvVarsCredential(c.credentialType)) return sum;
      const envVars = c.credentialsJson?.envVars;
      return sum + (Array.isArray(envVars) ? envVars.length : 0);
    }, 0) || 0;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    reset({
      name: environment.name,
      color: environment.color || ENV_COLORS[0],
    });
    setEditDialogOpen(true);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleTogglePause = async () => {
    handleMenuClose();
    try {
      await togglePause({
        tenantId,
        appId,
        envId: environment.id,
        paused: !environment.pauseSchedules,
      }).unwrap();
    } catch (err) {
      logger.error('Failed to toggle pause:', err);
    }
  };

  const handleSaveEdit = async (data: EditEnvironmentFormData) => {
    try {
      await updateEnvironment({
        tenantId,
        appId,
        envId: environment.id,
        data: {
          id: environment.id,
          name: data.name,
          color: data.color,
          isProd: environment.isProd,
          index: environment.index,
        },
      }).unwrap();
      setEditDialogOpen(false);
    } catch (err) {
      logger.error('Failed to update environment:', err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEnvironment({
        tenantId,
        appId,
        envId: environment.id,
      }).unwrap();
      setDeleteDialogOpen(false);
    } catch (err) {
      logger.error('Failed to delete environment:', err);
    }
  };

  return (
    <>
      <Card
        sx={{
          mb: 0,
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
        }}
      >
        {/* Card content (clickable) */}
        <CardContent
          onClick={onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            py: 1.5,
            px: 2,
            '&:last-child': { pb: 1.5 },
          }}
        >
          {/* Color indicator */}
          <Box
            sx={{
              width: 6,
              height: 30,
              borderRadius: 0.5,
              backgroundColor: environment.color || ENV_COLORS[0],
              mr: 2,
            }}
          />

          {/* Environment info */}
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>
                {environment.name}
              </Typography>
              {environment.isProd && (
                <Chip
                  label="Production"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '11px',
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              )}
              {environment.pauseSchedules && (
                <Chip
                  label="Paused"
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '11px' }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                {connectorCount} Connector{connectorCount !== 1 ? 's' : ''}
              </Typography>
              {envVarsCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                  {envVarsCount} Environment Variable{envVarsCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Actions */}
          <IconButton onClick={handleMenuOpen} size="small" sx={{ mr: 0.5 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleTogglePause}>
          <ListItemIcon>
            {environment.pauseSchedules ? (
              <PlayArrowIcon fontSize="small" />
            ) : (
              <PauseIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{environment.pauseSchedules ? 'Resume' : 'Pause'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Environment</DialogTitle>
        <form onSubmit={handleSubmit(handleSaveEdit)}>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Environment Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={
                      errors.name?.message || 'Use letters, numbers, underscores, and hyphens only'
                    }
                  />
                )}
              />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Color
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Controller
                    name="color"
                    control={control}
                    render={({ field }) => (
                      <>
                        {ENV_COLORS.map((color) => (
                          <Box
                            key={color}
                            onClick={() => field.onChange(color)}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              backgroundColor: color,
                              cursor: 'pointer',
                              border: editColor === color ? '3px solid #6C63FF' : '1px solid #ccc',
                            }}
                          />
                        ))}
                      </>
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setEditDialogOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!isValid || isUpdating}>
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setDeleteDialogOpen(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Environment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{environment.name}</strong>? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
